<script>
  // import { tweened } from "svelte/motion";
  import { tweened } from "./../tweened-staggered";
  import { cubicOut } from "svelte/easing";
  import { extent, max, min } from "d3-array";
  import { easeCubicOut } from "d3-ease";
  import { format } from "d3-format";
  import { scaleLinear, scaleLog, scaleSequential, scaleSqrt } from "d3-scale";
  import { interpolateRdYlBu } from "d3-scale-chromatic";
  import { interpolateHclLong } from "d3-interpolate";
  import { Delaunay } from "d3-delaunay";
  import Number from "./Number.svelte";
  import Icon from "./Icon.svelte";

  import { getDistanceBetweenPoints } from "./../utils";
  import rawData from "./../data/ISO_countrynames_2100data.csv";
  // import countryStatsRaw from "./../data/country_stats.csv";
  import countryNames from "./../data/countryNames.json";

  let svgElement;
  // let iterationsSinceUpdate;
  let xScale;
  let yScale;
  // let isAnimating = true;

  export let dims = [];
  export let yScaleLabels = [];
  export let xScaleLabel = "";

  const data = rawData
    .map(d => ({
      // ...d,
      country: d["ISO"],
      name: d["Countryname"],
      deathrate: +d["2100 death rate per 100,000"],
      income: +d["2100 income per capita (2019 USD)"],
      temperature: +d["2100 Long-run average annual temp (F)"],
      population: +d["2100 population (millions)"],
      pct_gdp: +d["adaptation_as_percent_gdp"]
    }))
    .filter(
      d =>
        d.population > 1 &&
        !["ATF", "BVT", "CL-", "HMD", "IOT", "SGS", "SP-"].includes(
          d["country"]
        )
    )
    .sort((a, b) => a.deathrate - b.deathrate);

  const bubbleSize = 6;

  let width = 700;
  let height = 700;

  let hoveredBubble = null;

  let bubbles = tweened(
    data.map((d, i) => ({
      x: 0,
      y: 0,
      r: 0,
      i
    })),
    {
      duration: 1200,
      delay: 0,
      easing: cubicOut,
      iDelay: 2
    }
  );

  $: xAccessor = d => d[dims[0]];
  $: radiusAccessor = d => d["population"];
  $: yAccessor = d => (dims[1] ? d[dims[1]] : 0);
  $: colorAccessor = d => d[dims[2]];

  const populations = data.map(d => +d.population);
  const radiusScale = scaleSqrt()
    .domain([Math.max(min(populations), 1), max(populations)])
    .range([height / 300, height / 20]);

  const deathrateExtent = extent(data, d => d.deathrate);
  const deathrateMax = Math.max(-deathrateExtent[0], deathrateExtent[1]);
  $: xScale = scaleLinear(
    dims[0] == "deathrate"
      ? [-deathrateMax, deathrateMax]
      : extent(data, xAccessor),
    [width * 0.1, width * 0.9]
  );
  $: midX = xScale(0);

  $: xTicks = xScale.ticks(6).map((x, i) => [x ? x : xScaleLabel, xScale(x)]);

  $: yExtent = extent(data, yAccessor);
  $: yScale =
    dims[1] == "income"
      ? scaleLog([400, yExtent[1]], [height - 20, 20])
      : scaleLinear(
          yExtent,
          dims[1] ? [height - 20, 20] : [height / 2, height / 2]
        );

  $: yTicks = yScale
    .ticks(dims[1] == "income" ? 2 : 6)
    .map(d => [d, yScale(d)]);

  $: colorScale = scaleSequential(interpolateRdYlBu).domain(
    extent(data, colorAccessor).reverse()
  );
  $: colorTicks = colorScale.ticks(20).map(d => [d, colorScale(d)]);

  let delaunay;
  const updateBubbles = () => {
    const updatedBubbles = data.map((d, i) => ({
      x: xScale(xAccessor(d)) || 0,
      y: yScale(yAccessor(d)) || 0,
      r: radiusScale(radiusAccessor(d) || 0),
      i
      // color: colorScale(colorAccessor(d))
    }));
    // // .sort((a, b) => b.r - a.r);

    bubbles.set(updatedBubbles);
    delaunay = Delaunay.from(
      updatedBubbles,
      d => d.x,
      d => d.y
    );
  };
  $: dimsString = dims.join(",");
  $: dimsString, width, updateBubbles();

  $: onMouseMove = e => {
    if (!delaunay) return;
    if (!delaunay.find) return;

    const x = e.clientX - svgElement.getBoundingClientRect().left;
    const y = e.clientY - svgElement.getBoundingClientRect().top;

    const mousePosition = [x, y];
    const pointIndex = delaunay.find(...mousePosition);
    if (pointIndex == -1) {
      hoveredBubble = null;
      return null;
    }

    const distance = getDistanceBetweenPoints(mousePosition, [
      $bubbles[pointIndex].x,
      $bubbles[pointIndex].y
    ]);
    if (distance > $bubbles[pointIndex].r + 50) {
      hoveredBubble = null;
      return;
    }

    hoveredBubble = { ...data[pointIndex], ...$bubbles[pointIndex] } || {};
  };
  const metricFormats = {
    income: "$,.0f",
    temperature: d => format(",.0f")(d) + "°F",
    pct_gdp: d => format(",.0f")(d) + "%"
  };
</script>

<figure
  class="c"
  bind:clientWidth="{width}"
  bind:clientHeight="{height}"
  on:mouseout="{() => (hoveredBubble = null)}">
  <svg
    style="{`width: ${width}px; height: ${height}px`}"
    bind:this="{svgElement}">
    <rect
      {width}
      {height}
      on:mousemove="{onMouseMove}"
      fill="transparent"></rect>
    {#each [...$bubbles].sort((a, b) => b.r - a.r) as d, i (d.i)}
      <circle
        cx="{d.x}"
        cy="{d.y}"
        r="{d.r}"
        fill="{dims[2] ? colorScale(colorAccessor(data[d.i])) : '#77e2ed'}"
        on:mousemove="{() => (hoveredBubble = { ...d, ...data[d.i] })}"></circle>
    {/each}
    {#if hoveredBubble}
      <circle
        cx="{hoveredBubble.x}"
        cy="{hoveredBubble.y}"
        r="{hoveredBubble.r}"
        fill="none"
        stroke="white"
        style="pointer-events: none"></circle>
    {/if}
  </svg>
  <!-- <div class="y-ticks">
    {#each yTicks as [label, offset], i (i)}
      <div class="y-tick" style="{`transform: translateY(${offset}px)`}">
        {#if label && isFinite(label)}
          <Number
            number="{label}"
            format="{metricFormats[dims[1]] || ',.0f'}" />
        {/if}
      </div>
    {/each}
  </div> -->
  <div class="y-labels">
    {#each yScaleLabels.filter(d => d) as label, i (i)}
      <div class="{`y-label y-label--${i ? 'top' : 'bottom'}`}">
        <div class="icon">
          <Icon name="arrow" />
        </div>
        <div class="text">{label}</div>
      </div>
    {/each}
  </div>
  <div class="x-ticks">
    {#each xTicks as [label, offset], i (i)}
      <div class="x-tick" style="{`transform: translateX(${offset}px)`}">
        {#if isFinite(label)}
          <Number
            number="{label}"
            format="{metricFormats[dims[0]] || ',.0f'}" />
        {:else}
          <span>{label}</span>
        {/if}
      </div>
    {/each}
  </div>
  {#if dims.includes('color')}
    <div class="legend">
      <div class="legend-title">Average Temperature</div>
      <div
        class="legend-fill"
        style="{`background: linear-gradient(to right, ${colorTicks
          .map(([_, color]) => color)
          .join(', ')})`}"></div>
    </div>
  {/if}

  {#if hoveredBubble}
    <div
      class="tooltip"
      style="{`transform: translate(calc(-50% + ${hoveredBubble.x}px), calc(-100% + -${hoveredBubble.r}px + ${hoveredBubble.y}px))`}">
      <div class="tooltip-title">{hoveredBubble.name}</div>
      <div class="tooltip-info">
        {#if hoveredBubble.deathrate > 0}+{/if}
        <Number
          number="{hoveredBubble.deathrate}"
          format=",.0f"
          duration="50" />
        per 100,000 people
      </div>
    </div>
    <div
      class="hovered-bubble"
      style="{[`transform: translate(calc(-50% + ${hoveredBubble.x}px), calc(-50% + ${hoveredBubble.y}px))`, `height: ${hoveredBubble.r * 2 + 2}px`, `width: ${hoveredBubble.r * 2 + 2}px`].join('; ')}"></div>
  {/if}
</figure>

<style>
  .c {
    width: 90vw;
    height: 90vh;
    position: absolute;
    margin: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: all;
  }
  @media (max-width: 800px) {
    .c {
      width: 98vw;
    }
  }
  .canvas-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }
  canvas {
    /* position: relative; */
    /* z-index: 10; */
  }
  svg {
    position: relative;
    z-index: 10;
  }
  circle {
    stroke: black;
  }
  .x-label {
    position: absolute;
    bottom: 1em;
    left: 50%;
  }
  .y-labels {
    position: absolute;
    top: 30%;
    bottom: 45%;
    right: 2em;
    display: flex;
    flex-direction: column-reverse;
    justify-content: space-between;
    text-align: right;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.8em;
  }
  .y-label {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .y-label .icon {
    width: 1.3em;
    height: 1.5em;
    transform-origin: center center;
  }
  .y-label--top .icon {
    transform: rotate(90deg);
  }
  .y-label--bottom .icon {
    transform: rotate(-90deg);
  }
  .y-label--bottom {
    flex-direction: column-reverse;
  }
  .x-ticks {
    position: absolute;
    bottom: 6%;
    left: 0;
    right: 0;
    /* color: #999898; */
  }
  .x-tick {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    text-align: center;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.8em;
    transition: all 0.3s ease-out;
    z-index: 2;
  }
  .x-tick > span {
    display: block;
    background: var(--bgc);
  }
  .x-tick:nth-of-type(3) {
    z-index: 10;
  }
  .y-ticks {
    position: absolute;
    top: 0;
    right: 2em;
    opacity: 0.5;
    text-align: right;
  }
  .y-tick {
    position: absolute;
    top: -3em;
    right: 0;
    /* font-weight: 700; */
    text-align: right;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.8em;
    transition: all 0.3s ease-out;
  }
  @keyframes fadeUp {
    0% {
      opacity: 0;
      transform: translateY(10px);
    }
    100% {
      opacity: 1;
      transform: none;
    }
  }
  .legend {
    position: absolute;
    top: 5em;
    right: 5em;
    display: flex;
    flex-direction: column;
    animation: fadeUp 0.3s ease-out;
    opacity: 0;
    transform: translateY(10px);
    animation-fill-mode: forwards;
  }
  .legend-title {
    font-size: 0.8em;
  }
  .legend-fill {
    height: 0.6em;
    width: 11em;
    border-radius: 3px;
  }
  .tooltip {
    position: absolute;
    top: -5px;
    left: 0;
    padding: 0.6em 1em;
    background: white;
    color: #000;
    font-size: 0.9em;
    box-shadow: var(--box-shadow);
    z-index: 100;
    transition: transform 0.13s ease-out;
    pointer-events: none;
  }
  .tooltip-title {
    font-weight: 900;
  }
  .hovered-bubble {
    position: absolute;
    top: 0;
    left: 0;
    height: 1px;
    width: 1px;
    border-radius: 100%;
    border: 2px solid;
    z-index: 100;
    pointer-events: none;
  }
</style>
